/**
 * Student Hub Gamification Service
 * Lightweight XP, Levels, Badges & Streaks system
 * All data stored in localStorage (no paywall, no addiction patterns)
 */

const STORAGE_KEY = 'wassla.student.gamification';
const DAILY_CAP_KEY = 'wassla.student.dailyCaps';

// XP rewards configuration
export const XP_REWARDS = {
  POST_BOOK: 20,
  COMPLETE_PROFILE: 15,
  SAVE_BOOK: 3,
  SHARE_LISTING: 5,
  DAILY_VISIT: 2,
};

// Daily caps for certain actions
export const DAILY_CAPS = {
  SAVE_BOOK: 10,      // Max 10 saves per day = 30 XP
  SHARE_LISTING: 20,  // Max 20 shares per day = 100 XP
  DAILY_VISIT: 1,     // Only 1 per day
};

// Badge definitions
export const BADGES = {
  book_starter: {
    id: 'book_starter',
    emoji: '📚',
    name: { en: 'Book Starter', ar: 'بداية الكتب' },
    description: { 
      en: 'Post your first book', 
      ar: 'انشر أول كتاب لك' 
    },
    requirement: { type: 'books_posted', count: 1 },
  },
  consistent_learner: {
    id: 'consistent_learner',
    emoji: '🔁',
    name: { en: 'Consistent Learner', ar: 'ملتزم بالتعلّم' },
    description: { 
      en: 'Visit Student Hub 7 days', 
      ar: 'زر قسم الطلاب ٧ أيام' 
    },
    requirement: { type: 'streak_days', count: 7 },
  },
  career_builder: {
    id: 'career_builder',
    emoji: '💼',
    name: { en: 'Career Builder', ar: 'جاهز للعمل' },
    description: { 
      en: 'Create your CV', 
      ar: 'أنشئ سيرتك الذاتية' 
    },
    requirement: { type: 'cv_created', count: 1 },
  },
  helper: {
    id: 'helper',
    emoji: '🤝',
    name: { en: 'Helper', ar: 'مُساند' },
    description: { 
      en: 'Share 5 listings', 
      ar: 'شارك ٥ إعلانات' 
    },
    requirement: { type: 'shares', count: 5 },
  },
  trusted_seller: {
    id: 'trusted_seller',
    emoji: '⭐',
    name: { en: 'Trusted Seller', ar: 'بائع موثوق' },
    description: { 
      en: 'Become a verified seller', 
      ar: 'كن بائعاً موثوقاً' 
    },
    requirement: { type: 'verified', count: 1 },
  },
  bookworm: {
    id: 'bookworm',
    emoji: '🐛',
    name: { en: 'Bookworm', ar: 'دودة كتب' },
    description: { 
      en: 'Post 5 books', 
      ar: 'انشر ٥ كتب' 
    },
    requirement: { type: 'books_posted', count: 5 },
  },
  explorer: {
    id: 'explorer',
    emoji: '🔍',
    name: { en: 'Explorer', ar: 'مستكشف' },
    description: { 
      en: 'Save 10 books', 
      ar: 'احفظ ١٠ كتب' 
    },
    requirement: { type: 'books_saved', count: 10 },
  },
};

// Academic levels
export const ACADEMIC_LEVELS = {
  secondary: { en: 'Secondary', ar: 'ثانوي' },
  university: { en: 'University', ar: 'جامعي' },
  postgrad: { en: 'Master / Postgrad', ar: 'دراسات عليا' },
  vocational: { en: 'Vocational', ar: 'مهني' },
};

/**
 * Get initial gamification state
 */
function getInitialState() {
  return {
    totalXP: 0,
    stats: {
      books_posted: 0,
      books_saved: 0,
      shares: 0,
      cv_created: 0,
      verified: 0,
      total_visits: 0,
    },
    badges: [],
    streak: {
      current: 0,
      lastVisitDate: null,
      longestStreak: 0,
    },
    academicLevel: null,
    profileComplete: false,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
}

/**
 * Load gamification data from localStorage
 */
export function loadGamificationData() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return getInitialState();
    const data = JSON.parse(raw);
    return { ...getInitialState(), ...data };
  } catch {
    return getInitialState();
  }
}

/**
 * Save gamification data to localStorage
 */
export function saveGamificationData(data) {
  try {
    const toSave = { ...data, updatedAt: new Date().toISOString() };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(toSave));
    return toSave;
  } catch {
    return data;
  }
}

/**
 * Get daily caps data
 */
function getDailyCaps() {
  try {
    const raw = localStorage.getItem(DAILY_CAP_KEY);
    if (!raw) return { date: null, counts: {} };
    return JSON.parse(raw);
  } catch {
    return { date: null, counts: {} };
  }
}

/**
 * Save daily caps data
 */
function saveDailyCaps(caps) {
  try {
    localStorage.setItem(DAILY_CAP_KEY, JSON.stringify(caps));
  } catch {}
}

/**
 * Check and reset daily caps if new day
 */
function checkDailyCaps() {
  const today = new Date().toDateString();
  const caps = getDailyCaps();
  
  if (caps.date !== today) {
    const newCaps = { date: today, counts: {} };
    saveDailyCaps(newCaps);
    return newCaps;
  }
  
  return caps;
}

/**
 * Check if action is within daily cap
 */
function isWithinDailyCap(actionType) {
  const caps = checkDailyCaps();
  const current = caps.counts[actionType] || 0;
  const limit = DAILY_CAPS[actionType];
  return limit ? current < limit : true;
}

/**
 * Increment daily cap counter
 */
function incrementDailyCap(actionType) {
  const caps = checkDailyCaps();
  caps.counts[actionType] = (caps.counts[actionType] || 0) + 1;
  saveDailyCaps(caps);
}

/**
 * Calculate level from XP
 * level = floor(totalXP / 100) + 1
 */
export function calculateLevel(totalXP) {
  return Math.floor(totalXP / 100) + 1;
}

/**
 * Calculate XP progress to next level
 */
export function calculateProgress(totalXP) {
  const currentLevelXP = totalXP % 100;
  return {
    current: currentLevelXP,
    needed: 100,
    percent: currentLevelXP,
  };
}

/**
 * Check and award badges based on stats
 */
function checkBadges(data) {
  const newBadges = [...data.badges];
  let badgeAwarded = null;
  
  Object.values(BADGES).forEach((badge) => {
    if (newBadges.includes(badge.id)) return;
    
    const { type, count } = badge.requirement;
    const currentCount = data.stats[type] || 0;
    
    if (type === 'streak_days' && data.streak.current >= count) {
      newBadges.push(badge.id);
      badgeAwarded = badge;
    } else if (currentCount >= count) {
      newBadges.push(badge.id);
      badgeAwarded = badge;
    }
  });
  
  return { badges: newBadges, newBadge: badgeAwarded };
}

/**
 * Update streak based on visit
 */
function updateStreak(data) {
  const today = new Date().toDateString();
  const lastVisit = data.streak.lastVisitDate;
  
  if (lastVisit === today) {
    // Already visited today
    return data.streak;
  }
  
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  const yesterdayStr = yesterday.toDateString();
  
  let newStreak = data.streak.current;
  
  if (lastVisit === yesterdayStr) {
    // Consecutive day
    newStreak += 1;
  } else if (lastVisit !== today) {
    // Streak broken, reset without guilt
    newStreak = 1;
  }
  
  return {
    current: newStreak,
    lastVisitDate: today,
    longestStreak: Math.max(data.streak.longestStreak, newStreak),
  };
}

/**
 * Award XP for an action
 * @param {string} action - Action type (POST_BOOK, SAVE_BOOK, etc.)
 * @returns {{ xpAwarded: number, newLevel: boolean, newBadge: object|null, data: object }}
 */
export function awardXP(action) {
  const data = loadGamificationData();
  const xpAmount = XP_REWARDS[action];
  
  if (!xpAmount) {
    return { xpAwarded: 0, newLevel: false, newBadge: null, data };
  }
  
  // Check daily cap
  if (!isWithinDailyCap(action)) {
    return { xpAwarded: 0, newLevel: false, newBadge: null, data, capped: true };
  }
  
  const oldLevel = calculateLevel(data.totalXP);
  
  // Award XP
  data.totalXP += xpAmount;
  incrementDailyCap(action);
  
  // Update relevant stat
  switch (action) {
    case 'POST_BOOK':
      data.stats.books_posted += 1;
      break;
    case 'SAVE_BOOK':
      data.stats.books_saved += 1;
      break;
    case 'SHARE_LISTING':
      data.stats.shares += 1;
      break;
    case 'COMPLETE_PROFILE':
      data.profileComplete = true;
      break;
    case 'DAILY_VISIT':
      data.stats.total_visits += 1;
      data.streak = updateStreak(data);
      break;
  }
  
  // Check for new badges
  const { badges, newBadge } = checkBadges(data);
  data.badges = badges;
  
  const newLevel = calculateLevel(data.totalXP);
  const leveledUp = newLevel > oldLevel;
  
  saveGamificationData(data);
  
  return { 
    xpAwarded: xpAmount, 
    newLevel: leveledUp, 
    newBadge, 
    data,
    level: newLevel,
  };
}

/**
 * Record daily visit (call on Student Hub mount)
 */
export function recordDailyVisit() {
  const today = new Date().toDateString();
  const data = loadGamificationData();
  
  // Only award XP once per day
  if (data.streak.lastVisitDate !== today) {
    return awardXP('DAILY_VISIT');
  }
  
  return { xpAwarded: 0, newLevel: false, newBadge: null, data };
}

/**
 * Set academic level
 */
export function setAcademicLevel(level) {
  const data = loadGamificationData();
  data.academicLevel = level;
  return saveGamificationData(data);
}

/**
 * Mark CV as created
 */
export function markCVCreated() {
  const data = loadGamificationData();
  data.stats.cv_created = 1;
  const { badges, newBadge } = checkBadges(data);
  data.badges = badges;
  saveGamificationData(data);
  return { newBadge, data };
}

/**
 * Mark user as verified seller
 */
export function markVerified() {
  const data = loadGamificationData();
  data.stats.verified = 1;
  const { badges, newBadge } = checkBadges(data);
  data.badges = badges;
  saveGamificationData(data);
  return { newBadge, data };
}

/**
 * Get user's unlocked badges
 */
export function getUnlockedBadges() {
  const data = loadGamificationData();
  return data.badges.map(id => BADGES[id]).filter(Boolean);
}

/**
 * Get all badges with unlock status
 */
export function getAllBadgesWithStatus() {
  const data = loadGamificationData();
  return Object.values(BADGES).map(badge => ({
    ...badge,
    unlocked: data.badges.includes(badge.id),
    progress: data.stats[badge.requirement.type] || 0,
  }));
}

export default {
  loadGamificationData,
  awardXP,
  recordDailyVisit,
  calculateLevel,
  calculateProgress,
  setAcademicLevel,
  markCVCreated,
  markVerified,
  getUnlockedBadges,
  getAllBadgesWithStatus,
  XP_REWARDS,
  BADGES,
  ACADEMIC_LEVELS,
};
